/**
 * Conditions and switches of the Logic view as entries of a handler's action
 * list, and their firmware form: an object in the event's command array
 * (docs/GPIO-LOGIC-FIRMWARE-SPEC.md §6).
 *
 *   { type: 'logicCondition', id, subject, conditions: [{ property, operator, value }], onChange, then: entries }
 *   { type: 'logicSwitch', id, subject, property, cases: [{ operator, value, then: entries }], else: entries, onChange }
 *
 * `subject` is what they compare: `{ kind: 'event' }` (the event payload),
 * `{ kind: 'gpio', entity }` or `{ kind: 'object', target: { pageId, objectId } }`.
 */

export const LOGIC_CONDITION_TYPE = 'logicCondition'
export const LOGIC_SWITCH_TYPE = 'logicSwitch'

const OPERATORS = new Set(['==', '!=', '>', '>=', '<', '<='])

export const isLogicFlowEntry = entry => entry?.type === LOGIC_CONDITION_TYPE || entry?.type === LOGIC_SWITCH_TYPE

/** The entries a flow entry can run, in order: its `then`, each case's, then `else`. */
function branchesOf(entry) {
    if (entry.type === LOGIC_CONDITION_TYPE) return [entry.then ?? []]
    return [...(entry.cases ?? []).map(item => item.then ?? []), entry.else ?? []]
}

/** Every action of an entry list, including those inside conditions and switches. */
export function flattenLogicEntries(entries) {
    return (entries ?? []).flatMap(entry => (isLogicFlowEntry(entry) ? branchesOf(entry).flatMap(flattenLogicEntries) : [entry]))
}

/** Wire form of a compared value: a number when it reads as one, 1/0 for on/off, else the text. */
export function conditionValue(value) {
    const text = String(value ?? '').trim()
    if (text !== '' && Number.isFinite(Number(text))) return Number(text)
    if (/^(on|true)$/i.test(text)) return 1
    if (/^(off|false)$/i.test(text)) return 0
    return text
}

function operand(subject, property) {
    if (!property) return null
    if (subject?.kind === 'gpio') return subject.entity ? { entity: subject.entity, prop: property } : null
    if (subject?.kind === 'object') {
        const pageId = Number(subject.target?.pageId)
        const objectId = Number(subject.target?.objectId)
        return Number.isInteger(pageId) && Number.isInteger(objectId) ? { obj: `p${pageId}b${objectId}`, prop: property } : null
    }
    return { prop: property }
}

/**
 * Firmware form of a condition or switch entry. `run(entries)` serializes a
 * branch. A branch-less result (nothing to run) is `{}`.
 * @returns {{ value?: object, error?: string }}
 */
export function serializeLogicFlowEntry(entry, run) {
    const edge = entry.onChange ? { edge: true } : {}
    if (entry.type === LOGIC_CONDITION_TYPE) {
        const conditions = (entry.conditions ?? []).map(item => {
            const left = operand(entry.subject, item?.property)
            return left && OPERATORS.has(item.operator) ? { ...left, op: item.operator, value: conditionValue(item.value) } : null
        })
        if (conditions.length === 0 || conditions.includes(null)) return { error: 'Condition is incomplete' }
        const then = run(entry.then)
        return then.length ? { value: { if: conditions, then, ...edge } } : {}
    }
    const left = operand(entry.subject, entry.property)
    if (!left || (entry.cases ?? []).some(item => !OPERATORS.has(item?.operator))) return { error: 'Switch is incomplete' }
    const cases = (entry.cases ?? []).map(item => ({ op: item.operator, value: conditionValue(item.value), then: run(item.then) }))
    const otherwise = run(entry.else)
    if (!cases.some(item => item.then.length) && otherwise.length === 0) return {}
    return { value: { switch: left, cases, ...(otherwise.length ? { else: otherwise } : {}), ...edge } }
}

/** Compare as numbers when both sides are numeric, else as text (== and != only). */
export function compareConditionValues(left, operator, right) {
    const a = conditionValue(left)
    const b = conditionValue(right)
    if (typeof a === 'number' && typeof b === 'number') {
        return { '==': a === b, '!=': a !== b, '>': a > b, '>=': a >= b, '<': a < b, '<=': a <= b }[operator] ?? false
    }
    if (operator === '==') return String(a) === String(b)
    if (operator === '!=') return String(a) !== String(b)
    return false
}

/**
 * The entries a flow entry runs for the preview, which knows the event
 * payload (`context`) and the objects (`context.resolveTarget`) but not the
 * GPIO entities: a GPIO comparison never holds there.
 */
export function previewLogicBranch(entry, context = {}) {
    const read = property => {
        if (entry.subject?.kind === 'gpio') return undefined
        if (entry.subject?.kind === 'object') {
            return context.resolveTarget?.(entry.subject.target?.pageId, entry.subject.target?.objectId)?.[property]
        }
        return context[property]
    }
    const holds = (property, operator, value) => {
        const current = read(property)
        return current !== undefined && compareConditionValues(current, operator, value)
    }
    if (entry.type === LOGIC_CONDITION_TYPE) {
        const all = (entry.conditions ?? []).every(item => holds(item.property, item.operator, item.value))
        return all ? entry.then ?? [] : []
    }
    return (entry.cases ?? []).find(item => holds(entry.property, item.operator, item.value))?.then ?? entry.else ?? []
}
